import React from 'react';

import { Box, MenuItem } from '@material-ui/core';
import { BorderBottom } from '@material-ui/icons';
import { ErrorMessage, FieldErrors } from 'react-hook-form';
import { FaRegCaretSquareDown } from 'react-icons/fa';

import { StyledSelect } from './styles';

import { TypographyCN } from 'components/TypographyCN';
import { theme } from 'styles';
import { useMobile } from 'utils/useMobile';

interface Options<T extends { id?: any; [key: string]: any } = any> {
  value?: T | string | number | string[];
  label: string;
}

type Props = {
  label?: string;
  options?: Options[];
  fullWidth?: boolean;
  labelProps?: React.ComponentProps<typeof TypographyCN>;
  valueProps?: React.ComponentProps<typeof StyledSelect>;
  boxHeight?: string;
  dropToDown?: boolean;
  name?: string;
  errorMessage?: string | FieldErrors<any>;
};

type SelectCNProps = Props & React.ComponentProps<typeof StyledSelect>;

export function SelectCN({
  label,
  options,
  fullWidth,
  labelProps,
  valueProps,
  boxHeight,
  errorMessage,
  dropToDown,
  value = '',
  fontSize,
  alignItems,
  ...other
}: SelectCNProps) {
  const downBreakPoint = useMobile();

  function FaRegCaretSquareDownStyled() {
    return (
      <FaRegCaretSquareDown
        className="MuiSvgIcon-root MuiSelect-icon"
        size={24}
        style={{ marginRight: '9px', color: errorMessage && '#D74545' }}
      />
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="space-between"
      style={{
        gap: '0.25rem',
      }}
    >
      {!!label && (
        <TypographyCN
          component="label"
          fontSize="0.875rem"
          setColor={theme.palette.primary.main}
          variant={downBreakPoint ? 'body1' : 'h6'}
          align="center"
          style={{
            textTransform: 'uppercase',
          }}
          {...labelProps}
        >
          {label}
        </TypographyCN>
      )}
      <StyledSelect
        IconComponent={FaRegCaretSquareDownStyled}
        disableUnderline={!Boolean(errorMessage)}
        fullWidth={fullWidth}
        variant="filled"
        MenuProps={
          dropToDown
            ? {
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }
            : undefined
        }
        value={value}
        error={errorMessage}
        inputProps={{
          underline: errorMessage && '1px solid red',
        }}
        {...valueProps}
        {...other}
      >
        {options?.map(({ label, value }, index) => (
          <MenuItem key={value?.id || index} value={value || index}>
            {label}
          </MenuItem>
        ))}
      </StyledSelect>
      {errorMessage && (
        <TypographyCN
          component="span"
          variant="body2"
          align="center"
          weight="lighter"
          setColor="#D74545"
          style={{ fontSize: 14, fontWeight: 700 }}
        >
          {typeof errorMessage === 'string' ? (
            errorMessage
          ) : (
            <ErrorMessage errors={errorMessage} name={other.name as string}>
              {({ message }) => message}
            </ErrorMessage>
          )}
        </TypographyCN>
      )}
    </Box>
  );
}
